import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import TypeSet from '../base_typeset/component';
import Icon from '../base_svg-icon/component';
import { Button } from 'audamatic-ui';
import Location from './_location/component';
import Map from '../ui_store-map/component';
import CheckBox from '../ui_checkbox/component';
import Context from '@context/store-finder';
import { getDevice, capitalize, isBrowser, log } from '@utils';
import Text from '@text';
import { t } from '@utils/i18n';
import gps from '../../images/current-location.png';

import './styles/styles.scss';

class SearchBlock extends Component {
  constructor(props) {
    super(props);

    const basket = this.props.store.getBasket();
    const storedQuery = isBrowser ? (localStorage.getItem('storeQuery') || basket.invoiceToAddress?.city || '') : '';
    this.state = {
      locationAllowed: true,
      query: storedQuery,
      storedPosition: null,
      showNoResults: false,
      closest: null,
      gpsSignal: false,
      hasScrolled: false,
      nowOpen: false,
    };

    this.listRef = createRef();
    this.maxNumberOfResults = 20;
  }

  componentDidMount() {
    this.context.setNoResultsMethod(this.showNoResults);
    this.context.setClearSearchMethod(this.clearSearch);
  }

  /**
   * Show the 'no results' message and set the closest store
   * @param {object} closest
   */
  showNoResults = (closest = null, reset = false) => {
    if (reset) {
      this.setState({
        showNoResults: true,
        closest,
      });
    } else {
      this.context.setFilteredStores([], true, () => {
        this.setState({
          showNoResults: true,
          closest,
        });
      });
    }
  };

  /**
   * Reset the search functionality
   */
  clearSearch = (callback = () => {}) => {
    this.setState({
      showNoResults: false,
      closest: null,
    }, callback);
  };

  /**
   * Handle search query input
   * @param {event} e
   */
  handleInput = (e) => {
    const { value: query } = e.target;
    this.setState({ query });
  };

  /**
   * Listen to 'Enter' while typing a query
   * @param {event} e
   */
  handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      this.submitSearch();
      e.target.blur();
    }
  };

  submitSearch = () => {
    const { query = '' } = this.state;
    if (!query.replace(/\s/g, '').length || this.querySetByGps) {
      localStorage.removeItem('storeQuery');
    } else {
      localStorage.setItem('storeQuery', query);
    }
    this.clearSearch(this.handleSearch);
  };

  /**
   * Select the closest store from the 'no results' message and set that
   * store as the active store. Also reload map with that store.
   * @param {event} e
   */
  selectClosest = () => {
    const { closest } = this.state;
    const { setActiveStore, reloadMap } = this.context;
    setActiveStore(closest);
    return this.clearSearch(() => {
      closest.forceActiveState();

      return reloadMap({
        lat: closest.latitude || closest.attributes.latitude,
        lng: closest.longitude || closest.attributes.longitude,
      });
    });
  };

  /**
   * Prior to calling the google API, check if the query has an exact match with one of the store.
   * Initially check if the query contains 4 digits and try to match a postalcode on the first 3 digits
   * Otherwise try to match on the city
   * @returns {array|boolean} filtered list  or false
   */
  checkExactMatches = () => {
    const { query = '' } = this.state;
    const { stores, reloadMap, setActiveStore, setSearching } = this.context;

    const exactMatches = stores.filter((store) => {
      // Check if postalcode, if so match on first 3 numbers
      const regex = /\d{4}/g;
      if (query.search(regex) >= 0) {
        const queryPostalCode = query.match(regex)[0].substring(0, 4);
        const storePostalCode = store.postalCode.substring(0, 4);
        return storePostalCode === queryPostalCode;
      }
      return store.city === query.toUpperCase();
    });

    if (!!exactMatches.length) {
      const firstResult = exactMatches[0];
      setActiveStore({});
      this.clearSearch(() => {
        reloadMap({
          lat: firstResult.attributes.latitude,
          lng: firstResult.attributes.longitude,
        });
        setSearching(false);
      });
      return true;
    }

    return false;
  };

  get querySetByGps() {
    return (this.state.query === t('storefinder.search.useLocation.query'));
  }

  /**
   * Handle the submit button or when the user presses 'Enter'
   * return false if no query is set, else check for exact match else call google api
   */
  handleSearch = () => {
    const { query = '' } = this.state;
    const { searchMethod = () => {}, setSearching } = this.context;

    // Ignore request
    if (!query && !this.querySetByGps) {
      return false;
    }
    if (this.querySetByGps) {
      return this.fetchMyLocation();
    }
    setSearching(true);
    if (!this.checkExactMatches()) {
      searchMethod(query);
    }
  };

  /**
   * Update the mnap with my position
   * @param {object} latlng
   */
  handleMyLocation = (latlng) => {
    const { activeStore, setActiveStore, reloadMap, clearSearch } = this.context;
    clearSearch(() => {
      reloadMap(latlng, true);

      this.setState({
        query: t('storefinder.search.useLocation.query'),
        locationAllowed: true,
      });

      if (!!activeStore.storeId) {
        activeStore.deactivate();
        setActiveStore({});
      }
    });
  };

  /**
   * Get the users location using the geolocation browser api
   * If allowed update the map, else show denied message
   */
  fetchMyLocation = () => {
    if (this.state.storedPosition) {
      this.setState({ gpsSignal: true }, () => {
        setTimeout(() => this.setState({ gpsSignal: false }), 1000);
      });
      return this.handleMyLocation(this.state.storedPosition);
    }

    this.setState({ gpsSignal: true });
    log('[Location] Requesting permission to read location');
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        this.handleMyLocation(coords);
        this.setState({
          storedPosition: coords,
          gpsSignal: false,
        });
        log('[Location] Permission granted');
      }, () => {
        this.setState({
          locationAllowed: false,
          gpsSignal: false,
        });
        log('[Location] Permission denied');
      });
    } else {
      this.setState({
        locationAllowed: false,
        gpsSignal: false,
      });
      log('[Location] Permission denied');
    }
  };

  /**
   * If the query is set to 'Mijn locatie' because the user requested their location, clear
   * the input before the user starts typing.
   */
  handleFocus = () => {
    if (this.state.query === t('storefinder.search.useLocation.query')) {
      this.setState({ query: '' });
    }
  };

  handleCheckbox = (nowOpen) => {
    this.setState({ nowOpen }, () => {
      this.context.toggleOpenMethod(nowOpen);
    });
  };

  /**
   * Check if the user has scrolled trough the list of results, if so, display
   * a box shadow for the disclaimer.
   */
  checkScrollPosition = () => {
    const { isMobile } = getDevice();
    if (!isMobile) {
      this.setState({ hasScrolled: (this.listRef?.current?.scrollTop > 0) });
    }
  };

  handleStockOnlyChange = () => {
    const { stockOnly, setStockOnly, initiated } = this.context;

    setStockOnly(!stockOnly, () => {
      if (initiated) {
        this.submitSearch();
      }
    });
  };

  /**
   * Get all unique city names in the list of stores
   * used to prevent duplicates in the datalist
   */
  get uniquePlaces() {
    const { stores } = this.context;
    const places = [];
    stores.forEach((store) => {
      const city = capitalize(store.city);
      if (!places.includes(city)) {
        places.push(city);
      }
    });
    return places.sort();
  }

  get minimalSearchBar() {
    const { query } = this.state;
    const { googleLoaded } = this.context;
    return (
      <>
        <TypeSet bold><Text>storefinder.search.title</Text></TypeSet>
        <div className="search-field">
          <input
            list="cities"
            value={query}
            placeholder={t('storefinder.search.input.placeholder')}
            onChange={this.handleInput}
            onKeyPress={this.handleKeyPress}
            onFocus={this.handleFocus}
            data-test-id="searchbar-stores"
            disabled={!googleLoaded}
          />

          {query.length > 2 && !query.match(/[0-9]/gi) && (
            <datalist id="cities">
              {this.uniquePlaces.map(place => (
                <option key={place} value={place}/>
              ))}
            </datalist>
          )}

          <Button
            variant="ghost"
            onClick={this.handleSearch}
            disabled={!googleLoaded}
            iconLeft={<Icon size={87} name="ico-search" />}
            data-test-id="searchbar-stores-submit"
          />
        </div>
      </>
    );
  }

  get extendedSearchBar() {
    const { query } = this.state;
    const { googleLoaded, searching } = this.context;

    return (
      <div className="extended-search-field">
        <label htmlFor="cities"><Text>storefinder.search.input.label</Text><sup>*</sup></label>
        <div className="flex-container">
          <input
            id="cities"
            list="cities"
            value={query}
            placeholder={t('storefinder.search.input.placeholder')}
            onChange={this.handleInput}
            onKeyPress={this.handleKeyPress}
            onFocus={this.handleFocus}
            data-test-id="searchbar-stores"
            disabled={!googleLoaded}
          />

          {query.length > 2 && !query.match(/[0-9]/gi) && (
            <datalist id="cities">
              {this.uniquePlaces.map(place => (
                <option key={place} value={place}/>
              ))}
            </datalist>
          )}

          <Button
            variant="primary"
            size="large"
            onClick={this.submitSearch}
            data-test-id="searchbar-stores-submit"
            disabled={!googleLoaded}
            loading={searching}
          >
            <Text>stock.search.buttonText</Text>
          </Button>
        </div>
      </div>
    );
  }

  render() {
    const { height, showMap, panel, mockMap, extendedSearch } = this.props;
    const { locationAllowed, showNoResults, closest, gpsSignal, hasScrolled } = this.state;
    const { initiated, listView, toggleView, filteredStores, activeStore, stockOnly } = this.context;
    const { isMobile } = getDevice();
    const showList = initiated && !!filteredStores.length;

    return (
      <div className={`${!!panel ? 'search-block' : 'flat'} ${initiated ? 'active' : 'idle'}`}>

        <div className={!!panel ? 'search-panel' : ''}>

          {extendedSearch ? this.extendedSearchBar : this.minimalSearchBar}

          <div className="map-actions">
            <div className={locationAllowed ? 'enabled' : 'disabled'} onClick={this.fetchMyLocation}>
              {locationAllowed ? (
                <div className="gps-toggle">
                  <TypeSet tag="div" scale="0">
                    <Text>storefinder.search.useLocation</Text>
                  </TypeSet>
                  {gpsSignal && (<img src={gps} className="gps-signal" width="16" height="16"/>)}
                </div>
              ) : (
                <TypeSet scale="0"><Text>storefinder.search.useLocation.failed</Text></TypeSet>
              )}
            </div>
            {(showList && !!panel) && (
              <div onClick={toggleView}>
                <TypeSet scale="0">
                  {t(listView ? 'storefinder.search.display.map' : 'storefinder.search.display.list')}
                </TypeSet>
              </div>
            )}
          </div>
        </div>


        {!panel && (
          <div className="stock-only-check">
            <CheckBox
              id="stock-check"
              data-test-id="checkbox-stock-only"
              onChange={this.handleStockOnlyChange}
              checked={stockOnly}
              label={t('stock.toggleAll.label')}
            />
          </div>
        )}

        {(showList || showNoResults) && (
          <div className={`results-buffer ${(hasScrolled && !showNoResults) ? 'show-shadow' : ''}`}/>
        )}

        {showNoResults && !filteredStores.length && (
          <div className="no-search-results">
            {isMobile ? (
              <span className="no-search-results-mobile">
                <TypeSet tag="div" bold>
                  <Text>storefinder.search.noResults.title.mobile</Text>
                </TypeSet>
                {listView && (
                  <div onClick={toggleView}>
                    <TypeSet tag="div">
                      <span dangerouslySetInnerHTML={{ __html: t('storefinder.search.noResults.tip.mobile') }} />
                    </TypeSet>
                  </div>
                )}
              </span>
            ) : (
              <span>
                <TypeSet tag="div" tight bold>
                  <Text>storefinder.search.noResults.title</Text>
                </TypeSet>
                <TypeSet  tag="div" tight>
                  <Text>storefinder.search.noResults.subtitle</Text>
                </TypeSet>
                {closest && (
                  <div className="closest-store" onClick={this.selectClosest}>
                    <TypeSet tight>
                      <Text>storefinder.search.noResults.tip</Text>&nbsp;
                    </TypeSet>
                    <span className="store-suggestion">
                      {capitalize(closest.city)}
                    </span>
                  </div>
                )}
              </span>
            )}
          </div>
        )}

        {(showMap && (isMobile || !!mockMap)) && (
          <Map height={height} show={!!panel && isMobile && !listView}/>
        )}

        {showList && (
          <div
            ref={this.listRef}
            className="search-list"
            onScroll={this.checkScrollPosition}
            data-test-id="storefinder-searchlist"
          >
            {!!activeStore.storeId ? (
              <Location key={activeStore.storeId} details={activeStore} showStock={!panel}/>
            ) : (
              <>
                {filteredStores.slice(0, this.maxNumberOfResults).map(details => (
                  <Location key={details.storeId} details={details} showStock={!panel}/>
                ))}
              </>
            )}
          </div>
        )}

      </div>
    );
  }
}

SearchBlock.contextType = Context;

SearchBlock.propTypes = {
  store: PropTypes.object,
  height: PropTypes.number,
  showMap: PropTypes.bool,
  panel: PropTypes.bool,
  mockMap: PropTypes.bool,
  extendedSearch: PropTypes.bool,
};

export default SearchBlock;
